import { StyledConfig } from 'styled-components';
import {
  parseToHsl,
  lighten,
  readableColor,
  darken,
  getContrast,
  hsl,
  modularScale
} from 'polished';
import { ContrastScores } from 'polished/lib/types/color';
import { ModularScaleRatio } from 'polished/lib/types/modularScaleRatio';

import { tryCatch } from '../utils';

export const getHoverColors = (color: string) => {
  const backgroundLightness = tryCatch(
    () => parseToHsl(color).lightness,
    () => 1
  );
  let hoverColor = tryCatch(() => lighten(0.1, color));
  let hoverContrastColor = tryCatch(() => readableColor(lighten(0.1, color)));
  if (backgroundLightness > 0.35) {
    hoverColor = tryCatch(() => darken(0.1, color));
    hoverContrastColor = tryCatch(() => readableColor(darken(0.1, color)));
  }

  return { background: hoverColor, foreground: hoverContrastColor };
};

const wcagContrast: Record<keyof ContrastScores | 'AANonText', number> = {
  // https://www.w3.org/TR/WCAG21/#contrast-minimum
  AA: 4.5,
  AALarge: 3,
  // https://www.w3.org/TR/WCAG21/#contrast-enhanced
  AAA: 7,
  AAALarge: 4.5,
  // https://www.w3.org/TR/WCAG21/#non-text-contrast
  AANonText: 3
};

export const readableHue = (
  color1: string,
  color2: string,
  {
    mode = 'both',
    level = 'AA'
  }: { mode?: 'light' | 'dark' | 'both'; level?: keyof typeof wcagContrast | number } = {}
): string => {
  const minContrast = typeof level === 'string' ? wcagContrast[level] : level;

  if (getContrast(color1, color2) >= minContrast) return color1;

  const { lightness, ...hueSaturation } = parseToHsl(color1);

  let shouldLighten = true;
  let shouldDarken = true;

  let lightEnough = '';
  let darkEnough = '';

  for (let i = 0; i <= 1; i += 0.01) {
    if (shouldLighten) {
      const lightented = hsl({
        ...hueSaturation,
        lightness: Math.min(lightness + i, 1)
      });

      if (getContrast(lightented, color2) >= minContrast) {
        if (mode !== 'dark') return lightented;

        shouldLighten = false;
        lightEnough = lightented;
      }

      if (lightness + i >= 1) shouldLighten = false;
    }

    if (shouldDarken) {
      const darkened = hsl({
        ...hueSaturation,
        lightness: Math.max(lightness - i, 0)
      });

      if (getContrast(darkened, color2) >= minContrast) {
        if (mode !== 'light') return darkened;

        shouldDarken = false;
        darkEnough = darkened;
      }

      if (lightness - i <= 0) shouldDarken = false;
    }

    if (!shouldLighten && !shouldDarken) break;
  }

  if (lightEnough) return lightEnough;
  if (darkEnough) return darkEnough;

  return readableColor(color2);
};

export const omitProps: <O extends object = Record<PropertyKey, unknown>>(
  ...props: PropertyKey[]
) => StyledConfig<O> = (...props) => ({
  shouldForwardProp: (prop, defaultValidatorFn) => !props.includes(prop) && defaultValidatorFn(prop)
});

export type FontSize = 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';
const stepsMap = {
  xxs: -2,
  xs: -1,
  s: 0,
  m: 1,
  l: 2,
  xl: 3,
  xxl: 4
};

export const calculateFontSize = (baseFontSize: string, baseFontScale: string | number) => {
  if (baseFontScale === 'linear')
    return Object.fromEntries(
      Object.entries(stepsMap).map(([size, steps]) => [
        size,
        `calc(${baseFontSize} + (${steps} * 0.0625rem))`
      ])
    ) as Record<FontSize, string>;

  return Object.fromEntries(
    Object.entries(stepsMap).map(([size, steps]) => [
      size,
      tryCatch(
        () => modularScale(steps, baseFontSize, baseFontScale as ModularScaleRatio),
        () => baseFontSize
      )
    ])
  ) as Record<FontSize, string>;
};
